// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate, useParams } from '@tanstack/react-router';

// material ui icons
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';

// material ui
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
  Typography,
} from '@mui/material';

// api
import { useGetAlarmsFeedByAccountQuery } from '@/api/recalc/endpoints/alarmsApiSlice';

const AlarmNotifications = () => {
  const { t } = useTranslation('alarms');
  const { accountId } = useParams({ strict: false });
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleNotificationClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewAlarms = () => {
    navigate({ to: '/$accountId/alarms' });
    handleClose();
  };

  const open = Boolean(anchorEl);

  const {
    data: alarmFeed,
    isLoading: isLoadingAlarmFeed,
    isFetching: isFetchingAlarmFeed,
  } = useGetAlarmsFeedByAccountQuery(accountId, {
    skip: !accountId,
  });

  const unresolvedCount = alarmFeed?.length || 0;
  const hasUnresolvedAlarms = unresolvedCount > 0;
  const isLoading = isLoadingAlarmFeed || isFetchingAlarmFeed;

  // Get first 3 alarms and calculate remaining
  const firstThreeAlarms = alarmFeed?.slice(0, 3) || [];
  const remainingCount = Math.max(0, unresolvedCount - 3);

  return (
    <>
      {isLoading ? (
        <Box sx={{ p: 1 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <IconButton
          onClick={handleNotificationClick}
          sx={{
            borderRadius: 2,
            // backgroundColor: alpha('#ff0000', 0.1),
            // ':hover': {
            //   backgroundColor: alpha('#ff0000', 0.2),
            // },
          }}
        >
          <Badge
            variant="dot"
            color="error"
            overlap="circular"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            invisible={!hasUnresolvedAlarms}
          >
            {/* <NotificationsNoneRoundedIcon sx={{ color: '#ff0000' }} /> */}
            <NotificationsNoneRoundedIcon />
          </Badge>
        </IconButton>
      )}

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        sx={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={8}
            sx={{
              mt: 1,
              minWidth: 280,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {t('notifications.title')}
              </Typography>

              {hasUnresolvedAlarms ? (
                <>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 1 }}
                  >
                    {t('notifications.unresolved_count', {
                      count: unresolvedCount,
                    })}
                  </Typography>

                  <List dense sx={{ mt: 1, mb: 0 }}>
                    {firstThreeAlarms.map((alarm, index) => (
                      <ListItem key={alarm.AlarmId || index} disablePadding>
                        <ListItemText
                          primary={
                            alarm.Title ||
                            alarm.AlarmType ||
                            t('notifications.unnamed_alarm')
                          }
                          slotProps={{
                            primary: {
                              variant: 'body2',
                              noWrap: true,
                              sx: { fontSize: '0.875rem' },
                            }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  {remainingCount > 0 && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mt: 1, fontStyle: 'italic' }}
                    >
                      {t('notifications.more_to_inspect', {
                        count: remainingCount,
                      })}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 1 }}
                >
                  {t('notifications.no_unresolved')}
                </Typography>
              )}
            </Box>

            {hasUnresolvedAlarms && (
              <>
                <Divider />
                <Box sx={{ p: 2 }}>
                  <Button
                    variant="contained"
                    disableElevation
                    fullWidth
                    onClick={handleViewAlarms}
                    size="small"
                  >
                    {t('notifications.view_all')}
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default AlarmNotifications;
